/**
 * We don't want always store widget structure in the db.
 * Instead, we store a reference that can be resolved into a structure.
 */

import { toHex } from "common/colors";

export const widgetReferences = {
    trendline: {
        fee_rate: {
            chart: {
                series: [{
                    name: "Effective rate",
                    key: "fee_rate",
                    type: "area"
                }]
            },
            showSummary: true
        },
        fees_vs_sales: {
            chart: {
                yAxis: [{
                    gridLineWidth: 1,
                    gridLineDashStyle: "Dash",
                    title: { text: "" },
                    labels: {
                        format: "{value}%",
                        style: {
                            color: toHex("green-mid")
                        }
                    }
                }, {
                    opposite: true,
                    min: 0,
                    gridLineWidth: 0,
                    valueType: "amount",
                    allowDecimals: false,
                    title: { text: "" }
                }],
                series: [{
                    name: "Total effective rate",
                    key: "fee_rate",
                    valueType: "percentage",
                    type: "line",
                    extend: {
                        zIndex: 2
                    }
                }, {
                    name: "Sales",
                    key: "sales_amount",
                    valueType: "amount",
                    type: "column",
                    yAxis: 1,
                    below: 0,
                    color: toHex("gray-soft"),
                    extend: {
                        zIndex: 1
                    }
                }]
            },
            showSummary: false
        },
        settled_cash: {
            chart: {
                yAxis: [{
                    min: 0,
                    gridLineWidth: 1,
                    gridLineDashStyle: "Dash",
                    title: { text: "" }
                }, {
                    gridLineWidth: 0,
                    opposite: true,
                    title: { text: "" }
                }],
                series: [{
                    name: "Settled cash",
                    key: "total_amount",
                    valueType: "amount",
                    type: "line",
                    yAxis: 0
                }, {
                    name: "Transactions",
                    key: "transactions_count",
                    valueType: "count",
                    type: "column",
                    yAxis: 1,
                    below: 0
                }]
            },
            showSummary: false
        },
        chargeback_rate: { // Keep old name of the widget reference
            showSummary: true,
            showChart: false
        },
        chargeback_count: {
            by: "count",
            chart: {
                series: [{
                    name: "Chargeback count",
                    key: "chargebacks_count",
                    type: "area"
                }]
            },
            showSummary: false
        },
        
        authorizations_all: {
            by: "count",
            chart: {
                series: [{
                    name: "Approvals",
                    key: "approvals_count",
                    type: "area"
                }, {
                    name: "Declines",
                    key: "declines_count",
                    type: "area"
                }, {
                    name: "All authorizations",
                    key: "auth_count",
                    type: "line"
                }]
            },
            showSummary: true
        },
        decline_rate: {
            declines: true,
            chart: {
                yAxis: [{
                    floor: 0,
                    ceiling: 100,
                    gridLineWidth: 1,
                    gridLineDashStyle: "Dash",
                    labels: {
                        format: "{value}%"
                    },
                    title: { text: "" }
                }],
                series: [{
                    name: "Decline rate",
                    key: "decline_rate",
                    valueType: "percentage",
                    type: "area"
                }]
            },
            showSummary: true
        },
        approval_rate: {
            chart: {
                yAxis: [{
                    floor: 0,
                    ceiling: 100,
                    gridLineWidth: 1,
                    gridLineDashStyle: "Dash",
                    labels: {
                        format: "{value}%"
                    },
                    title: { text: "" }
                }],
                series: [{
                    name: "Approval rate",
                    key: "approval_rate",
                    valueType: "percentage",
                    type: "area"
                }]
            },
            showSummary: true
        },
        refunds_count_amount: {
            chart: {
                yAxis: [{
                    min: 0,
                    gridLineWidth: 1,
                    gridLineDashStyle: "Dash",
                    title: { text: "" },
                    valueType: "amount"
                }, {
                    gridLineWidth: 0,
                    opposite: true,
                    title: { text: "" },
                    labels: {
                        style: {
                            color: toHex("blue-mid"),
                            fontWeight: "bold"
                        }
                    }
                }],
                series: [{
                    name: "Count",
                    key: "refunds_count",
                    valueType: "count",
                    type: "line",
                    yAxis: 1,
                    color: toHex("blue-mid"),
                    extend: {
                        zIndex: 2
                    }
                }, {
                    name: "Amount",
                    key: "refunds_amount",
                    valueType: "amount",
                    type: "column",
                    yAxis: 0,
                    color: toHex("gray-soft"),
                    extend: {
                        zIndex: 1
                    }
                }]
            },
            showSummary: true
        },
        sales_count_amount: {
            chart: {
                yAxis: [{
                    valueType: "amount",
                    min: 0,
                    gridLineWidth: 1,
                    gridLineDashStyle: "Dash",
                    allowDecimals: false,
                    title: { text: "" }
                }, {
                    gridLineWidth: 0,
                    opposite: true,
                    allowDecimals: false,
                    title: { text: "" },
                    labels: {
                        style: {
                            color: toHex("green-mid"),
                            fontWeight: "bold"
                        }
                    }
                }],
                series: [{
                    name: "Count",
                    key: "sales_count",
                    valueType: "count",
                    type: "line",
                    yAxis: 1,
                    color: toHex("green-mid"),
                    extend: {
                        zIndex: 2
                    }
                }, {
                    name: "Amount",
                    key: "sales_amount",
                    valueType: "amount",
                    type: "column",
                    yAxis: 0,
                    color: toHex("gray-soft"),
                    extend: {
                        zIndex: 1
                    }
                }]
            },
            showSummary: true
        },
        sales_amount: {
            chart: {
                yAxis: [{
                    allowDecimals: false,
                    title: { text: "" }
                }],
                series: [{
                    name: "Sales",
                    type: "line",
                    key: "sales_amount",
                    valueType: "amount"
                }]
            },
            by: "amount",
            showSummary: true
        },
        sales_count: {
            chart: {
                yAxis: [{
                    allowDecimals: false,
                    title: { text: "" }
                }],
                series: [{
                    name: "Sales",
                    type: "line",
                    key: "sales_count",
                    valueType: "count"
                }]
            },
            by: "count",
            showSummary: true
        }
    },
    map: {
        approval_rate: {
            pivot: "country",
            chart: {
                minValue: 0,
                tooltipData: [{
                    label: "All authorizations",
                    key: "auth_count",
                    valueType: "count"
                }, {
                    label: "Approval rate",
                    key: "approval_rate",
                    valueType: "percentage"
                }, {
                    label: "Approvals",
                    key: "approvals_count",
                    valueType: "count"
                }],
                series: [{
                    name: "Approval rate",
                    key: "approval_rate",
                    valueType: "percentage",
                    joinBy: ["iso-a3", "issuing_country"]
                }]
            }
        },
        decline_rate: {
            chart: {
                minValue: 0,
                series: [{
                    name: "Decline rate",
                    key: "decline_rate",
                    valueType: "percentage",
                    joinBy: ["iso-a3", "issuing_country"]
                }]
            }
        },
        authorizations_all: {
            by: "count",
            chart: {
                minValue: 0,
                tooltipData: [{
                    label: "All authorizations",
                    key: "auth_count",
                    valueType: "count"
                }, {
                    label: "Approval rate",
                    key: "approval_rate",
                    valueType: "percentage"
                }, {
                    label: "Approvals",
                    key: "approvals_count",
                    valueType: "count"
                }],
                series: [{
                    name: "All authorizations",
                    key: "auth_count",
                    joinBy: ["iso-a3", "issuing_country"]
                }]
            }
        }
    }
};
